import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { client } from 'src/graphql/client';
import { MOVE_DELIVERY_STOPS_TO_ROUTE } from 'src/graphql/requests/mutations';
import {
  DeliveryStopsMoveToRoutePayload,
  Route,
} from 'src/graphql/types/delivery';
import {
  GDeliveryStopsMoveToRouteInput,
  GMoveDeliveryStopsToRouteMutation,
} from 'src/graphql/types/generated';
import {
  CANNOT_CHANGE_DATA,
  firstUserErrorMessage,
  INVALID_NIGHT_END,
  isUserErrorInArray,
  updateSequenceNumbersRedelivery,
} from 'src/utils';
import { ModalType, showModal } from '../modal';
import { NotificationType, showNotification } from '../notification';
import { retireRoutesPoll } from './getRoutes';
import { DeliveryState } from './state';

type MoveDeliveryStopsToRouteArgs = GDeliveryStopsMoveToRouteInput & {
  // Callers that show their own outcome messaging (e.g. the 3PD unmark flow,
  // which reports on the whole unmark, not just the move) can opt out of this
  // thunk's own toast to avoid showing two, possibly conflicting, messages.
  suppressNotification?: boolean;
  returnFocusId?: string;
  // CSV batches share one editor, which closes only when every batch succeeds.
  keepEditorOpen?: boolean;
};

export const moveDeliveryStopsToRoute = createAsyncThunk(
  'delivery/moveDeliveryStopsToRoute',
  async (
    {
      routeId,
      stopIds,
      redelivery,
      suppressNotification,
      returnFocusId,
    }: MoveDeliveryStopsToRouteArgs,
    thunkAPI,
  ): Promise<DeliveryStopsMoveToRoutePayload | null> => {
    const { data, error } = await client
      .mutation<GMoveDeliveryStopsToRouteMutation>(
        MOVE_DELIVERY_STOPS_TO_ROUTE,
        {
          input: { routeId, stopIds, redelivery },
        },
      )
      .toPromise();
    if (
      error ||
      !data?.moveDeliveryStopsToRoute?.updatedRoutes ||
      data.moveDeliveryStopsToRoute.userErrors.length > 0
    ) {
      const userErrors = data?.moveDeliveryStopsToRoute?.userErrors ?? [];
      const blocked = userErrors.find(
        (userError) => userError.__typename === 'MetrobiAssignmentBlocked',
      );
      if (blocked?.__typename === 'MetrobiAssignmentBlocked') {
        thunkAPI.dispatch(
          showModal({
            type: ModalType.MetrobiIneligible,
            variables: {
              mode: 'move',
              offenders: blocked.offenders,
              returnFocusId,
            },
          }),
        );
        return null;
      }
      const isInvalidNightEnd = isUserErrorInArray(
        INVALID_NIGHT_END,
        userErrors,
      );
      // InvalidNightEnd keeps its own copy, which explains the cutoff rule
      // better than the backend's wording. Everything else now carries a typed
      // message (the document selects it on UserErrorInterface), so show it
      // rather than a generic failure: the customer-pickup moves can fail for
      // reasons only the server knows, such as an already-completed stop.
      const message = isInvalidNightEnd
        ? CANNOT_CHANGE_DATA
        : userErrors.length
        ? firstUserErrorMessage(userErrors)
        : undefined;
      if (!suppressNotification) {
        thunkAPI.dispatch(
          showNotification({
            type: NotificationType.Error,
            message,
          }),
        );
      }
      return null;
    }
    if (!suppressNotification) {
      thunkAPI.dispatch(
        showNotification({
          type: NotificationType.Success,
          message: `Moved stop(s) successfully`,
        }),
      );
    }
    const moveDeliveryStopsToRoute: DeliveryStopsMoveToRoutePayload =
      data.moveDeliveryStopsToRoute;
    return moveDeliveryStopsToRoute;
  },
);

export const moveDeliveryStopsToRouteFulfilled = (
  state: DeliveryState,
  action: PayloadAction<DeliveryStopsMoveToRoutePayload | null> & {
    meta?: { arg?: { keepEditorOpen?: boolean } };
  },
): void => {
  state.changeRouteEditor.isSaving = false;
  if (action?.payload?.updatedRoutes) {
    retireRoutesPoll(state);
    if (!action.meta?.arg?.keepEditorOpen)
      state.secondaryCardVisible = undefined;

    // update redelivery stop sequence numbers
    const updatedRoutes = action.payload?.updatedRoutes.map(
      updateSequenceNumbersRedelivery,
    );
    const currentRoutesState = state.routes ? [...state.routes] : [];

    // Update routes
    state.routes = currentRoutesState.map((route: Route): Route => {
      // update the route in the routes array via replacement
      const myUpdatedRoute = updatedRoutes.find(
        (updatedRoute) => updatedRoute.id === route.id,
      );
      if (myUpdatedRoute) {
        return myUpdatedRoute;
      }
      return route;
    });
    state.changeRouteEditor.selectedStops = [];
  }
};

export const moveDeliveryStopsToRoutePending = (state: DeliveryState): void => {
  state.changeRouteEditor.isSaving = true;
};
